import { useQuery } from '@tanstack/react-query'
import { ALL_FEATURE_KEYS, type FeatureKey } from '@academy/config'
import { api } from './api.js'

export type FeatureMap = Record<FeatureKey, boolean>

function allDisabled(): FeatureMap {
  const map = {} as FeatureMap
  for (const key of ALL_FEATURE_KEYS) map[key] = false
  return map
}

/**
 * Resolved feature map for the signed-in user's academy.
 *
 * Source of truth is the server-resolved map (plan features + per-academy
 * plan_override) exposed at GET /api/v1/academy/features. Only academy-scoped
 * roles (academy_admin, teacher) have an academy context, so callers should
 * only enable the query for those roles — super_admin and not-yet-onboarded
 * admins have no academy and the endpoint would 403.
 *
 * While loading, every flag reads false so disabled UI never flashes in before
 * the real map arrives.
 */
export function useFeatures(enabled = true): {
  features: FeatureMap
  isLoading: boolean
} {
  const query = useQuery({
    queryKey: ['academy-features'],
    enabled,
    // Short window + refetch-on-focus so a super-admin plan/override change
    // surfaces in the academy admin's sidebar promptly, instead of being masked
    // for up to 5 minutes by a stale cache.
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<FeatureMap> => {
      const res = await api<Partial<FeatureMap>>('/api/v1/academy/features')
      const base = allDisabled()
      if ('data' in res && res.data) {
        for (const key of ALL_FEATURE_KEYS) {
          base[key] = res.data[key] === true
        }
      }
      return base
    },
  })

  return {
    features: query.data ?? allDisabled(),
    isLoading: enabled && query.isLoading,
  }
}
